import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaSave, 
  FaPlus, 
  FaTrash, 
  FaEye, 
  FaEyeSlash, 
  FaLink, 
  FaSpinner,
  FaGithub,
  FaLinkedin,
  FaTwitter,
  FaInstagram,
  FaYoutube,
  FaFacebook,
  FaGlobe,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { useSocialLinks } from '../../context/SocialLinksContext';

const SocialLinksEditor = () => {
  const { socialLinks, updateSocialLinks } = useSocialLinks();
  const [links, setLinks] = useState(socialLinks);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Platform-specific icons and colors
  const getPlatformIcon = (platform) => {
    const icons = {
      github: FaGithub,
      linkedin: FaLinkedin,
      twitter: FaTwitter,
      instagram: FaInstagram,
      youtube: FaYoutube,
      facebook: FaFacebook
    };
    return icons[platform] || FaGlobe;
  };

  const getPlatformColor = (platform) => {
    const colors = {
      github: 'text-gray-300',
      linkedin: 'text-blue-400',
      twitter: 'text-blue-400',
      instagram: 'text-pink-400',
      youtube: 'text-red-400',
      facebook: 'text-blue-500'
    };
    return colors[platform] || 'text-gray-300';
  };

  const getPlatformLabel = (platform) => {
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  const validateUrl = (url) => {
    if (!url) return true; // Empty URLs are allowed
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    setLinks(socialLinks);
  }, [socialLinks]);

  const handleChange = (platform, idx, value) => {
    setLinks(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        links: prev[platform].links.map((l, i) => (i === idx ? value : l))
      }
    }));

    // Validate URL
    const isValid = validateUrl(value);
    setErrors(prev => ({
      ...prev,
      [`${platform}-${idx}`]: isValid ? null : 'Please enter a valid URL'
    }));
  };

  const handleAddLink = (platform) => {
    setLinks(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        links: [...prev[platform].links, '']
      }
    }));
  };

  const handleRemoveLink = (platform, idx) => {
    setLinks(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        links: prev[platform].links.filter((_, i) => i !== idx)
      }
    }));
  };

  const handleToggleVisible = (platform) => {
    setLinks(prev => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        visible: !prev[platform].visible
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setErrors({});

    // Validate all URLs before saving
    const newErrors = {};
    let hasErrors = false;

    Object.keys(links).forEach(platform => {
      links[platform].links.forEach((link, idx) => {
        if (link && !validateUrl(link)) {
          newErrors[`${platform}-${idx}`] = 'Please enter a valid URL';
          hasErrors = true;
        }
      });
    });

    if (hasErrors) {
      setErrors(newErrors);
      setMessage('Please fix the invalid URLs before saving');
      setSaving(false);
      return;
    }

    try {
      await updateSocialLinks(links);
      setMessage('Social links updated successfully!');
    } catch (error) {
      setMessage('Failed to update links');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-900 rounded-2xl p-8 shadow-lg max-w-6xl mx-auto relative"
    >
      <h3 className="text-2xl font-bold text-primary mb-8 text-center flex items-center justify-center gap-3">
        <FaLink className="text-primary" />
        Social Links Manager
      </h3>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-6 p-4 rounded-lg border ${
            message.includes('Failed') || message.includes('fix')
              ? 'bg-red-900/20 text-red-300 border-red-800'
              : 'bg-green-900/20 text-green-300 border-green-800'
          }`}
        >
          {message}
        </motion.div>
      )}

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence>
          {Object.keys(links).map(platform => {
            const PlatformIcon = getPlatformIcon(platform);
            const platformColor = getPlatformColor(platform);
            const platformLabel = getPlatformLabel(platform);
            const hasVisibleLinks = links[platform].links.some(link => link.trim() !== '');
            
            return (
              <motion.div
                key={platform}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className={`bg-gray-800 rounded-xl p-6 border transition-all duration-200 ${
                  links[platform].visible 
                    ? 'border-gray-600 hover:border-gray-500' 
                    : 'border-gray-700 opacity-75'
                }`}
              >
                {/* Platform Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gray-700 ${platformColor}`}>
                      <PlatformIcon className="text-xl" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{platformLabel}</h4>
                      <div className="flex items-center gap-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          links[platform].visible 
                            ? 'bg-green-900/30 text-green-400' 
                            : 'bg-gray-700 text-gray-400'
                        }`}>
                          {links[platform].visible ? 'Visible' : 'Hidden'}
                        </span>
                        <span className="text-gray-400">
                          {links[platform].links.filter(link => link.trim() !== '').length} links
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleVisible(platform)}
                    className={`p-2 rounded-lg transition-colors ${
                      links[platform].visible 
                        ? 'text-green-400 hover:text-green-300 hover:bg-green-900/20' 
                        : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700'
                    }`}
                    title={`${links[platform].visible ? 'Hide' : 'Show'} ${platformLabel}`}
                  >
                    {links[platform].visible ? <FaEye /> : <FaEyeSlash />}
                  </button>
                </div>

                {/* Links List */}
                <div className="space-y-3">
                  <AnimatePresence>
                    {links[platform].links.map((link, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 10 }}
                        className="space-y-1"
                      >
                        <div className="flex items-center gap-2">
                          <div className="flex-1 relative">
                            <input
                              type="url"
                              value={link}
                              onChange={e => handleChange(platform, idx, e.target.value)}
                              placeholder={`Enter ${platformLabel} URL`}
                              className={`w-full px-4 py-3 rounded-lg bg-gray-700 text-white placeholder-gray-400 border transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
                                errors[`${platform}-${idx}`] 
                                  ? 'border-red-500 focus:ring-red-500' 
                                  : 'border-gray-600 focus:border-transparent'
                              }`}
                            />
                            {link && (
                              <a
                                href={link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-primary transition-colors"
                                title="Open link"
                              >
                                <FaExternalLinkAlt size={14} />
                              </a>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveLink(platform, idx)}
                            className="p-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                            title="Remove link"
                          >
                            <FaTrash size={14} />
                          </button>
                        </div>
                        {errors[`${platform}-${idx}`] && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-red-400 text-xs bg-red-900/20 border border-red-800 rounded px-2 py-1"
                          >
                            {errors[`${platform}-${idx}`]}
                          </motion.div>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  {/* Add Link Button */}
                  <button
                    onClick={() => handleAddLink(platform)}
                    className="w-full mt-4 px-4 py-3 rounded-lg bg-primary/20 text-primary border border-primary/30 hover:bg-primary/30 transition-colors flex items-center justify-center gap-2"
                  >
                    <FaPlus />
                    Add {platformLabel} Link
                  </button>
                </div>

                {/* Empty State */}
                {!hasVisibleLinks && (
                  <div className="mt-4 text-center py-4 text-gray-400">
                    <FaLink className="text-2xl mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No {platformLabel} links added yet</p>
                  </div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8 pt-6 border-t border-gray-700">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-3 disabled:opacity-50 transition-colors font-medium"
        >
          {saving ? (
            <>
              <FaSpinner className="animate-spin" />
              Saving Changes...
            </>
          ) : (
            <>
              <FaSave />
              Save All Social Links
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default SocialLinksEditor;
